import { useEffect, useRef, useState } from "react";
import { productPath } from "../content/roadshow";
import { recordRoadshowEvent } from "../lib/analytics";

const MOBILE_PRODUCT_PATH_QUERY = "(max-width: 720px)";

function shouldBringPreviewIntoView() {
  return (
    document.documentElement.classList.contains("roadshow-reading") &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(MOBILE_PRODUCT_PATH_QUERY).matches
  );
}

export function ProductPath() {
  const [active, setActive] = useState(0);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const programmaticScrollTarget = useRef<number | null>(null);
  const programmaticScrollTimer = useRef<number | null>(null);
  const current = productPath[active];

  function selectStep(index: number) {
    setActive(index);
    scrollTrackToIndex(index);
    if (shouldBringPreviewIntoView()) {
      previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    recordRoadshowEvent({
      sectionId: "product",
      questionId: "product-path-step",
      choice: productPath[index].id,
      eventType: "choice"
    });
  }

  function scrollTrackToIndex(index: number) {
    const track = trackRef.current;
    const slide = slideRefs.current[index];
    if (!track || !slide) return;

    if (programmaticScrollTimer.current) {
      window.clearTimeout(programmaticScrollTimer.current);
    }

    programmaticScrollTarget.current = index;
    if (typeof track.scrollTo === "function") {
      track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
    } else {
      track.scrollLeft = slide.offsetLeft;
    }

    programmaticScrollTimer.current = window.setTimeout(() => {
      programmaticScrollTarget.current = null;
    }, 720);
  }

  function syncStepFromTrack() {
    if (programmaticScrollTarget.current !== null) return;
    const track = trackRef.current;
    if (!track) return;

    const trackCenter = track.scrollLeft + track.clientWidth / 2;
    let nextIndex = active;
    let closestDistance = Number.POSITIVE_INFINITY;

    slideRefs.current.forEach((slide, index) => {
      if (!slide) return;
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(trackCenter - slideCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        nextIndex = index;
      }
    });

    if (nextIndex !== active) {
      setActive(nextIndex);
    }
  }

  useEffect(() => {
    return () => {
      if (programmaticScrollTimer.current) {
        window.clearTimeout(programmaticScrollTimer.current);
      }
    };
  }, []);

  return (
    <div className="product-path" data-local-swipe>
      <div className="path-steps">
        {productPath.map((step, index) => (
          <button
            key={step.id}
            type="button"
            className={active === index ? "is-active" : ""}
            onClick={() => selectStep(index)}
          >
            <span>{String(index + 1).padStart(2, "0")}</span>
            {step.label}
          </button>
        ))}
      </div>
      <div ref={previewRef} className="path-preview">
        <figure className="product-screenshot-frame">
          <div
            ref={trackRef}
            className="product-screenshot-track"
            data-local-swipe
            aria-label="刷题宝产品页面截图轮播"
            onScroll={syncStepFromTrack}
          >
            {productPath.map((step, index) => (
              <div
                key={step.id}
                ref={(node) => {
                  slideRefs.current[index] = node;
                }}
                className="product-screenshot-slide"
              >
                <img src={step.screenshot} alt={`刷题宝页面截图：${step.title}`} />
              </div>
            ))}
          </div>
        </figure>
        <div className="path-copy">
          <p className="eyebrow">Product Screen</p>
          <h3>{current.title}</h3>
          <p>{current.text}</p>
        </div>
      </div>
    </div>
  );
}
